import date from 'date-and-time';

/** The three views of the Calendar (design a1, a2, c1). */
export type CalendarView = 'month' | 'week' | 'list';

export const calendarViews: readonly CalendarView[] = ['month', 'week', 'list'];

/** One day of a grid, in the local time of the browser. */
export interface CalendarDay {
  /** `YYYY-MM-DD`, local. */
  key: string;
  /** Local midnight. */
  start: Date;
  /** The day is in the month of the anchor (Month view). Always true in Week and List. */
  isInPeriod: boolean;
  isToday: boolean;
  /** The day is before today. */
  isPast: boolean;
}

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** The local `YYYY-MM-DD` of a time. */
export function dayKey(moment: Date): string {
  return date.format(moment, 'YYYY-MM-DD');
}

/** Local midnight of a `YYYY-MM-DD`, or null when the text is not a day. */
export function parseDayKey(key: string | null | undefined): Date | null {
  const match = DAY_KEY.exec(key ?? '');
  if (match === null) {
    return null;
  }
  const day = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(day.getTime()) || dayKey(day) !== key ? null : day;
}

export function startOfDay(moment: Date): Date {
  return new Date(moment.getFullYear(), moment.getMonth(), moment.getDate());
}

/** The Monday of the week of a day. */
export function startOfWeek(moment: Date): Date {
  const day = startOfDay(moment);
  const fromMonday = (day.getDay() + 6) % 7;
  return date.addDays(day, -fromMonday);
}

export function startOfMonth(moment: Date): Date {
  return new Date(moment.getFullYear(), moment.getMonth(), 1);
}

function makeDay(start: Date, isInPeriod: boolean, now: Date): CalendarDay {
  const today = startOfDay(now).getTime();
  return {
    key: dayKey(start),
    start,
    isInPeriod,
    isToday: start.getTime() === today,
    isPast: start.getTime() < today,
  };
}

/** The 42 days (6 rows, Monday first) of the month of `anchor`. */
export function monthGridDays(anchor: Date, now: Date = new Date()): CalendarDay[] {
  const first = startOfMonth(anchor);
  const gridStart = startOfWeek(first);
  return Array.from({ length: 42 }, (_unused, index) => {
    const start = date.addDays(gridStart, index);
    return makeDay(start, start.getMonth() === first.getMonth(), now);
  });
}

/** The 7 days (Monday first) of the week of `anchor`. */
export function weekDays(anchor: Date, now: Date = new Date()): CalendarDay[] {
  const gridStart = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_unused, index) => makeDay(date.addDays(gridStart, index), true, now));
}

/**
 * The days a view shows. The List view shows the month of the anchor, from its first day, so the
 * list and the month read the same posts.
 */
export function viewDays(view: CalendarView, anchor: Date, now: Date = new Date()): CalendarDay[] {
  if (view === 'week') {
    return weekDays(anchor, now);
  }
  if (view === 'list') {
    const first = startOfMonth(anchor);
    const count = daysInMonth(first);
    return Array.from({ length: count }, (_unused, index) => makeDay(date.addDays(first, index), true, now));
  }
  return monthGridDays(anchor, now);
}

function daysInMonth(first: Date): number {
  return new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
}

/** The range of the server read of a view: from the first day to the end of the last day. */
export function viewRange(days: CalendarDay[]): { from: string; to: string } {
  const first = days[0]?.start ?? startOfDay(new Date());
  const last = days[days.length - 1]?.start ?? first;
  return { from: first.toISOString(), to: date.addDays(last, 1).toISOString() };
}

/** The anchor one period earlier (-1) or later (+1). */
export function shiftAnchor(view: CalendarView, anchor: Date, direction: -1 | 1): Date {
  if (view === 'week') {
    return date.addDays(startOfWeek(anchor), 7 * direction);
  }
  return date.addMonths(startOfMonth(anchor), direction);
}

/** Minutes after local midnight of a time. */
export function minutesOfDay(moment: Date): number {
  return moment.getHours() * 60 + moment.getMinutes();
}

/** The time on a day at a number of minutes after its midnight. */
export function atMinutes(day: Date, minutes: number): Date {
  const moment = startOfDay(day);
  moment.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return moment;
}

/** `HH:mm` of a time, for the Composer handoff. */
export function timeKey(moment: Date): string {
  return date.format(moment, 'HH:mm');
}
